import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import { gsap } from 'gsap';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import { match2BuruStreakRewards } from 'src/replicant/match2/match2.ruleset';
import StateObserver from 'src/StateObserver';
import { lerp } from '../odie';
import Match2ProgressBar from './Match2ProgressBar';
import { getStreakRewardIcon } from '../match2.icons';

let currentStreak = 0;

export default class Match2BuruStreakPanel extends ImageScaleView {
  private bar!: Match2ProgressBar;
  private ticks: ImageScaleView[] = [];

  constructor({
    superview,
    x,
    y,
  }: {
    superview: View;
    x?: number;
    y?: number;
  }) {
    super({
      superview,
      x,
      y,
      width: 598,
      height: 231,
      image: 'assets/ui/match2/common/panel_burustreak.png',
      centerOnOrigin: true,
      centerAnchor: true,
      scaleX: 0,
    });

    new LangBitmapFontTextView({
      superview: this,
      localeText: () => i18n('match2.buruStreak.title'),
      x: this.style.width * 0.5,
      y: 30,
      font: 'Body',
      color: '#7A674F',
      size: 26,
      centerOnOrigin: true,
      centerAnchor: true,
      verticalAlign: 'center',
      align: 'center',
    });

    this.createBaseBar();
    this.createBarContent();
  }

  private createBaseBar() {
    const width = 474;
    const height = 37;

    this.bar = new Match2ProgressBar({
      superview: this,
      width,
      height,
      x: (this.style.width - width) * 0.5,
      y: (this.style.height - height) * 0.5 - 10,
    });
  }

  private createBarContent() {
    const rewards = match2BuruStreakRewards;
    const amount = rewards.length;

    const fillMargin = 7;
    const barWidth = this.bar.style.width - fillMargin * 2;
    const sectionWidth = barWidth / amount;

    for (let index = 0; index < amount; index++) {
      const reward = rewards[index];
      const targetX =
        this.bar.style.x + fillMargin + sectionWidth * (index + 1) - 22;
      const targetY = this.bar.style.y + this.bar.style.height * 0.5;

      if (index < amount - 1) {
        new View({
          superview: this,
          backgroundColor: '#40BA4C',
          width: 4,
          height: 74,
          x: targetX - 2,
          y: targetY,
        });
      }

      const panel = new ImageScaleView({
        superview: this,
        image: 'assets/ui/match2/common/streak_bg.png',
        width: 63,
        height: 61,
        centerOnOrigin: true,
        centerAnchor: true,
        x: targetX,
        y: targetY,
      });

      getStreakRewardIcon(reward)
        .get()
        .then((image) => {
          new ImageScaleView({
            superview: panel,
            image,
            width: panel.style.width,
            height: panel.style.height,
            centerOnOrigin: true,
            x: panel.style.width * 0.5,
            y: panel.style.height * 0.5,
            scale: 0.8,
          });
        });

      new LangBitmapFontTextView({
        superview: panel,
        localeText: () => `${index + 1}`,
        x: panel.style.width - 14,
        y: 5,
        font: 'Title',
        color: '#FFFFFF',
        size: 22,
        centerOnOrigin: true,
        centerAnchor: true,
        verticalAlign: 'center',
        align: 'center',
      });

      new LangBitmapFontTextView({
        superview: this,
        localeText: () =>
          i18n(`match2.buruStreak.win${reward.wins > 1 ? 's' : ''}`, {
            streak: reward.wins,
          }),
        x: targetX - sectionWidth * 0.5,
        y: targetY + 35,
        font: 'Body',
        color: '#7A674F',
        size: 26,
        centerOnOrigin: true,
        centerAnchor: true,
        verticalAlign: 'center',
        align: 'center',
      });

      this.ticks.push(
        new ImageScaleView({
          superview: this,
          image: 'assets/ui/match2/common/icon_tick2.png',
          width: 46,
          height: 44,
          centerOnOrigin: true,
          centerAnchor: true,
          x: targetX - sectionWidth * 0.5 + 10,
          y: targetY - 15,
          scale: 0,
        }),
      );
    }
  }

  public tick(): void {
    this.ticks.forEach((tick, index) => {
      const wins = match2BuruStreakRewards[index].wins;
      const ratio = this.getWinProgressRatio(wins);
      tick.style.scale = lerp(
        tick.style.scale,
        ratio - this.bar.fillWidthRatio < 0.025 ? 1 : 0,
        0.3,
      );
    });
  }

  private refreshProgress() {
    this.bar.progress = this.getWinProgressRatio();
  }

  public async show() {
    const streak = StateObserver.getState().user.match2.buruStreak.value;
    const useAnimation = streak !== currentStreak;

    this.refreshProgress();
    currentStreak = streak;
    await gsap.to(this.style, { scaleX: 1, duration: 0.2, ease: 'quad.inOut' });

    if (useAnimation) {
      await this.bar.animateProgress(this.getWinProgressRatio(), 1500);
    }
  }

  public flashProgress() {
    const opts = {
      opacity: 0,
      duration: 0.75,
      ease: 'quad.inOut',
      yoyo: true,
      repeat: -1,
    };

    gsap.to(this.bar['progressFill'].style, opts);
    this.ticks.forEach((tick) => gsap.to(tick.style, opts));
  }

  public unflashProgress() {
    gsap.killTweensOf(this.bar['progressFill'].style);
    this.ticks.forEach((tick) => gsap.killTweensOf(tick.style));
  }

  private getWinProgressRatio(wins = currentStreak) {
    const sectionIndex = this.getWinStreakSection(wins);
    if (sectionIndex === -1) return 1;

    const rewardSet = match2BuruStreakRewards[sectionIndex];
    const sectionRatio = 1 / match2BuruStreakRewards.length;
    const base = sectionIndex / match2BuruStreakRewards.length;

    if (sectionIndex === 0) {
      return base + sectionRatio * (currentStreak / rewardSet.wins);
    }

    const previousRewardSet = match2BuruStreakRewards[sectionIndex - 1];
    const sectionWins = currentStreak - previousRewardSet.wins;

    return base + sectionRatio * (sectionWins / rewardSet.wins);
  }

  private getWinStreakSection(wins: number) {
    return match2BuruStreakRewards.findIndex(
      (rewardSet) => wins < rewardSet.wins,
    );
  }
}
